import React from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import { useDialogManager } from '../../../../../widgets/dialog';
import { isConfirmedRun } from '../confirmTwice';
import { EditorDialog } from '../editorDialogWindow';

// UNA SOLA CONFERMA PER TOGLIERE, in tutte le liste della scena. Ce n'erano quattro, e ognuna
// chiedeva a modo suo: «Confirm removal?» negli oggetti, «Are you sure you want to remove the
// selected light(s)? This action cannot be undone.» nelle luci, un titolo che contava le camere
// e una quarta forma nei materiali. Quattro modi di fare la stessa domanda sono quattro cose da
// leggere, e chi legge la seconda non riconosce la prima.
//
// LA DOMANDA DICE COSA SPARISCE E QUANTO. «Are you sure?» non è una domanda: non nomina niente,
// e chi ha cliccato per sbaglio conferma lo stesso perché non ha modo di accorgersene. Qui si
// legge il nome quando il soggetto è uno — più preciso di un «1» — e il conteggio quando sono
// tanti, perché otto nomi in un dialogo sono un elenco e non una domanda.
//
// E DICE QUELLO CHE L'ELENCO NON MOSTRA: i figli che se ne vanno col ramo, gli oggetti che
// restano senza il materiale che portavano. Sono le due cose che si scoprirebbero dopo, quando
// non si può più tornare indietro.
//
// DAI BOTTONI E DAI MENU SI CHIEDE DUE VOLTE, SENZA FINESTRA (prototipo V3; utente, 2026-10-20): il comando
// diventa «Are you sure?» e il secondo clic rimuove (`confirmTwice`). Arrivati qui da lì, la domanda è già
// stata fatta e si rimuove subito. La finestra resta per chi arriva da un'altra strada.

const DIALOG_ID = 'remove-dialog';

/** «“Chair”» con uno solo, «3 objects» con più di uno. */
export const subjectPhrase = (names: string[], many: string): string => (names.length === 1 ? `“${names[0]}”` : `${names.length} ${many}`);

export interface RemovalRequest {
    /** I nomi di ciò che sparisce: uno si legge, molti si contano. */
    names: string[];
    /** Come si chiama la specie: «object» / «objects». */
    one: string;
    many: string;
    /** Quanti se ne vanno col ramo senza essere nominati. */
    inside?: number;
    /** La conseguenza che l'elenco non mostra: «3 objects are using it and will be left without». */
    consequence?: string;
    onConfirm: () => void;
}

/**
 * Restituisce la funzione che apre la conferma. Chi la chiama passa cosa sparisce e cosa fare
 * dopo il sì; il testo e la forma li mette questa.
 */
export const useRemovalPrompt = () => {
    const { showDialog, closeDialog } = useDialogManager();

    return (request: RemovalRequest) => {
        const { names, one, many, inside, consequence, onConfirm } = request;
        if (!names.length) return;
        if (isConfirmedRun()) {
            onConfirm();
            return;
        }

        const phrase = subjectPhrase(names, many);
        const what = inside ? stringFormat(T._('{0} and {1} {2} inside', 'Admin'), phrase, inside.toString(), inside === 1 ? one : many) : phrase;

        showDialog(
            DIALOG_ID,
            <EditorDialog
                title={T._('Remove', 'Admin')}
                size='small'
                // Si rinuncia dalla X in alto, come in tutte le finestre della scena (utente, 2026-10-14):
                // nel piede resta la sola azione. Esc fa quello che fa la X.
                closeOnEscape
                onClose={() => closeDialog(DIALOG_ID)}
                buttons={[
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            onConfirm();
                            closeDialog(DIALOG_ID);
                        }
                    }
                ]}
            >
                <p>{stringFormat(T._('Remove {0} from this scene?', 'Admin'), what)}</p>
                {consequence && <p>{consequence}</p>}
            </EditorDialog>
        );
    };
};
